/// Low-precision Sun position — declination and Greenwich Hour Angle
/// (GHA), good to roughly 1 arc-minute over a few years. Drawn from
/// the Astronomical Almanac's *approximate* solar position formulae,
/// the same set most teaching tools use. Good enough for sextant
/// practice and certainly fine for the syllabus.
///
/// We deliberately avoid full-precision Meeus / VSOP87 here — the
/// extra accuracy buys nothing on a sextant whose own resolution is
/// 0.1' on a perfect day, and the simpler maths is auditable in a
/// classroom.

import {
  DEG,
  RAD,
  daysSinceJ2000,
  degreesToHours,
  normalize360,
} from "./time";

export interface SunPosition {
  /// Declination of the Sun, degrees (positive = N, negative = S).
  decDeg: number;
  /// Greenwich Hour Angle of the Sun, degrees [0, 360).
  ghaDeg: number;
  /// Right ascension of the Sun, in decimal hours [0, 24).
  raHours: number;
  /// Greenwich Mean Sidereal Time, in decimal hours [0, 24). Useful
  /// to derive GHA Aries for the star sights of Phase 3.
  gmstHours: number;
  /// Equation of Time — apparent solar time minus mean solar time,
  /// in minutes of time. The wobble between sundial and clock.
  eotMinutes: number;
}

/// The Sun's position at a given UTC instant.
export function sunPosition(date: Date): SunPosition {
  const n = daysSinceJ2000(date);

  // Mean longitude and mean anomaly of the Sun (Astronomical Almanac
  // low-precision formulae). Degrees.
  const L = normalize360(280.46 + 0.9856474 * n);
  const g = normalize360(357.528 + 0.9856003 * n) * DEG;

  // Ecliptic longitude of the Sun.
  const lambda =
    (L + 1.915 * Math.sin(g) + 0.020 * Math.sin(2 * g)) * DEG;

  // Obliquity of the ecliptic — drifts almost imperceptibly over years.
  const epsilon = (23.439 - 0.0000004 * n) * DEG;

  // Equatorial coordinates.
  const raRad = Math.atan2(
    Math.cos(epsilon) * Math.sin(lambda),
    Math.cos(lambda),
  );
  const decDeg = Math.asin(Math.sin(epsilon) * Math.sin(lambda)) * RAD;
  const raHours = ((raRad * RAD) / 15 + 24) % 24;

  // Greenwich Mean Sidereal Time, low-precision form (United States
  // Naval Observatory's "good to 0.1 s" approximation).
  const gmstHours = ((18.697374558 + 24.06570982441908 * n) % 24 + 24) % 24;

  // GHA of the body = GHA of Aries − RA(body), all in degrees,
  // normalised to [0, 360). At Greenwich apparent noon (Sun on the
  // meridian) GHA_Sun = 0; six hours later it's 90° (Sun 90° west);
  // at midnight it's 180° (Sun on the antimeridian).
  const ghaDeg = normalize360((gmstHours - raHours) * 15);

  // Equation of Time = apparent solar time − mean solar time, in
  // minutes. Apparent solar time at Greenwich = (GHA + 180°)/15 hours
  // (apparent noon = GHA 0 = 12h); mean solar time = UT.
  const apparentSolarHours = (ghaDeg / 15 + 12) % 24;
  const utHours =
    date.getUTCHours() +
    date.getUTCMinutes() / 60 +
    date.getUTCSeconds() / 3600;
  let eotMinutes = (apparentSolarHours - utHours) * 60;
  while (eotMinutes > 12 * 60) eotMinutes -= 24 * 60;
  while (eotMinutes <= -12 * 60) eotMinutes += 24 * 60;

  return {
    decDeg,
    ghaDeg,
    raHours,
    gmstHours,
    eotMinutes,
  };
}

/// Local Hour Angle of the Sun at a given longitude. East longitude
/// positive. The convention in sight reduction.
export function sunLHA(date: Date, longitudeDegEast: number): number {
  const { ghaDeg } = sunPosition(date);
  return normalize360(ghaDeg + longitudeDegEast);
}

/// Convenience: the Sun's GHA expressed in hours (for students who'd
/// rather think in time than arc).
export function sunGHAHours(date: Date): number {
  return degreesToHours(sunPosition(date).ghaDeg);
}

/// The Sun's apparent semi-diameter in arc-minutes — varies through
/// the year as Earth's orbital distance changes. Peaks near perihelion
/// (~early January, ~16.3') and dips near aphelion (~early July,
/// ~15.7'). The Almanac average is 15.994' at 1 AU.
export function sunSemiDiameterArcmin(date: Date): number {
  const n = daysSinceJ2000(date);
  const g = normalize360(357.528 + 0.9856003 * n) * DEG;
  // Earth–Sun distance in AU, low-precision orbital expansion.
  const r = 1.00014 - 0.01671 * Math.cos(g) - 0.00014 * Math.cos(2 * g);
  return 15.994 / r;
}
